import { CancelToken } from 'axios';
import React, { FC, ReactNode, KeyboardEvent, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Segment } from '~src/interfaces/segment.interface';

import {
  HasSegmentRemoveHandler,
} from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/CustomChip';
import SvgIcon from '~src/shared/SvgIcon';
import SearchInput from '~src/shared/form/inputs/SearchInput';

import { ChipsWrapper, ChipsInputContainer } from './customChips.sc';

interface Props {
  renderChip: (chip: Segment & HasSegmentRemoveHandler) => ReactNode;
  renderItem: (selected: boolean, value: Segment, handleSelect: (val: Segment) => void) => JSX.Element;
  onChange: (item: Segment[]) => void;
  setInputRef: ((node: HTMLInputElement) => void) | null;
  inputPlaceholder: { key: string; value: string };
  fetchSearchSuggestions: (value: string, token: CancelToken, filter?: string) => Promise<Segment[]>;
  chipsData: Segment[];
  emptyMessage: { key: string; value: string };
  isFocused?: boolean;
  locked?: boolean;
  error?: boolean;
  filter?: string;
  blank?: boolean;
  excludeSegmentIds?: string[];
}

const CustomChips: FC<Props> = (props) => {
  const [chipsData, setChipsData] = useState(props.chipsData);
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setChipsData(props.chipsData);
  }, [props.chipsData]);

  function onKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && event.currentTarget.value === '') {
      const tmpChipsData = [...chipsData];
      tmpChipsData.pop();
      setChipsData(tmpChipsData);
      props.onChange(tmpChipsData);
    }
  }

  function addItem(item: Segment, callback: () => void) {
    const tmpChipsData = [...chipsData];
    tmpChipsData.push(item);
    setChipsData(tmpChipsData);
    props.onChange(tmpChipsData);
    callback();
    if (input.current) {
      input.current.focus();
    }
  }

  function removeChip(chip: Segment) {
    const tmpChipsData = [...chipsData];
    const filteredData = tmpChipsData.filter(item => item.id !== chip.id);
    setChipsData(filteredData);
    props.onChange(filteredData);
  }

  function renderChip(chip: Segment) {
    const tmpChip: Segment & HasSegmentRemoveHandler = {...chip, onRemove: removeChip};

    return props.renderChip(tmpChip);
  }

  function inputSetting(tmpInput: HTMLInputElement) {
    input.current = tmpInput;
    if (props.setInputRef) {
      props.setInputRef(tmpInput);
    }
  }

  function onKeyDownItem(event: KeyboardEvent<HTMLInputElement>) {
    onKeyPress(event);
  }

  function onClickItem() {
    if (input.current) {
      input.current.focus();
    }
  }

  const [t] = useTranslation();

  return (
    <ChipsInputContainer
      onKeyDown={onKeyDownItem}
      onClick={onClickItem}
      $locked={props.locked}
      $isFocused={props.isFocused}
      $error={props.error}
    >
      {!props.locked && !props.blank && <SvgIcon name="iconSearch" />}
      <ChipsWrapper $blank={props.blank} $locked={props.locked}>
        {props.chipsData && props.chipsData.map(renderChip)}
        {!props.locked && (
          <SearchInput
            t={t}
            fetchSearchSuggestions={props.fetchSearchSuggestions}
            minLength={1}
            inputClassName="chips-input body_text"
            debounceTimeout={250}
            handleSelectElement={addItem}
            renderListItem={props.renderItem}
            setInputRef={inputSetting}
            inputPlaceholder={props.inputPlaceholder}
            emptyMessage={props.emptyMessage}
            filter={props.filter}
            excludeSegmentIds={props.excludeSegmentIds}
          />
        )}
      </ChipsWrapper>
    </ChipsInputContainer>
  );
};

export default CustomChips;
